import LoaderInline from '@jetbrains/ring-ui/components/loader-inline/loader-inline'
import Message from '@jetbrains/ring-ui/components/message/message'
import {Directions} from '@jetbrains/ring-ui/components/popup/popup.consts'
import ordinal from 'ordinal'
import * as React from 'react'
import {useEffect, useReducer, useRef, useMemo} from 'react'

import {getBuildLocator} from '../../../rest/locators'
import {getBuildHref} from '../../../routes'
import {getBuildType} from '../../../selectors'
import {restApi} from '../../../services/rest'
import type {BuildId, BuildTypeId} from '../../../types'
import {truthy} from '../../../utils/guards'
import type {Unsubscribe} from '../../../utils/subscriber'
import {getBuildTypeSuffix, getTopics, subscribeOnOverallEvents} from '../../../utils/subscriber'
import * as SubscriptionEvents from '../../../utils/subscriptionEvents'
import RouterLink from '../RouterLink/RouterLink'

import styles from './RunBuild.css'

import {useAppDispatch, useAppSelector} from 'src/hooks/react-redux'

type Props = {
  buildTypeId: BuildTypeId
  startedBuildId?: BuildId
  setStartedBuildId?: (buildId?: BuildId) => void
}

export default function StartedBuildMessage({
  startedBuildId,
  buildTypeId,
  setStartedBuildId,
}: Props) {
  const dispatch = useAppDispatch()
  const buildTypeInternalId = useAppSelector(state => getBuildType(state, buildTypeId)?.internalId)
  const buildHref = startedBuildId != null ? getBuildHref(startedBuildId, buildTypeId) : null
  const unsubscribe = useRef<Unsubscribe>()
  const [fetchSingleBuildData, {buildFetched, buildState, queuePosition, buildNumber}] =
    restApi.endpoints.getBuild.useLazyQuery({
      selectFromResult: ({data}) => ({
        buildFetched: data != null,
        buildState: data?.state ?? 'queued',
        queuePosition: data?.queuePosition,
        buildNumber: data?.number,
      }),
    })
  const arg = useMemo(
    () =>
      startedBuildId != null
        ? {
            buildLocator: getBuildLocator(startedBuildId),
            fields: 'state,queuePosition,number',
          }
        : null,
    [startedBuildId],
  )
  useEffect(() => {
    if (startedBuildId != null) {
      unsubscribe.current = subscribeOnOverallEvents(
        [
          buildState === 'queued' && `events/${SubscriptionEvents.BUILD_QUEUE_ORDER_CHANGED};`,
          ...(buildTypeInternalId != null
            ? getTopics(
                getBuildTypeSuffix(buildTypeInternalId),
                [
                  buildState === 'queued' && SubscriptionEvents.BUILD_STARTED,
                  SubscriptionEvents.BUILD_FINISHED,
                  SubscriptionEvents.BUILD_INTERRUPTED,
                ].filter(truthy),
              )
            : []),
        ].filter(truthy),
        () => fetchSingleBuildData(arg!),
      )
    }
    return unsubscribe.current
  }, [startedBuildId, buildTypeInternalId, dispatch, buildState, fetchSingleBuildData, arg])

  // redraw on first fetch
  const [_, redraw] = useReducer(() => true, false)
  useEffect(() => {
    if (buildFetched && process.env.NODE_ENV !== 'test') {
      redraw()
    }
  }, [buildFetched])

  return (
    <Message
      icon={null}
      className={styles.popup}
      tailClassName={styles.popupTail}
      directions={[Directions.BOTTOM_LEFT]}
      key={startedBuildId}
      popupProps={{
        onCloseAttempt: () => setStartedBuildId?.(),
        offset: 12,
      }}
    >
      {startedBuildId != null ? (
        buildState === 'queued' ? (
          <>
            {'The build is '}
            {queuePosition != null && buildHref != null && (
              <>
                {'at '}
                <RouterLink to={buildHref}>{ordinal(queuePosition)}</RouterLink>
                {' position '}
              </>
            )}
            {'in queue'}
          </>
        ) : (
          <>
            {'The build '}
            {buildHref != null && <RouterLink to={buildHref}>{`#${buildNumber}`}</RouterLink>}
            {` is ${buildState}`}
          </>
        )
      ) : (
        <LoaderInline>{'Adding a build to queue'}</LoaderInline>
      )}
    </Message>
  )
}
